import type { ScaleOptions } from 'chart.js'
import type { AxisOptions, XAxisOptions, YAxisOptions } from '../../types'
import { formatTick } from '../formatTick'
import { formatTimeTick } from '../formatTimeTick'
import { isValidISODate } from '../isValidISODate'
import {
  DEFAULT_BORDER_OPTIONS,
  DEFAULT_GRID_OPTIONS,
  DEFAULT_TICK_OPTIONS,
  DEFAULT_TITLE_OPTIONS,
  TIME_SERIES_SCALE_GRID_OPTIONS,
  TIME_SERIES_SCALE_TICKS_OPTIONS,
  TIME_SERIES_SCALE_TIME_OPTIONS,
} from './constants'

function createScaleOptions(
  axis: AxisOptions | undefined,
  showGridLines: boolean,
  position: ScaleOptions<'linear' | 'time'>['position'] = 'left',
): ScaleOptions<'linear' | 'time'> | undefined {
  if (!axis) return undefined

  const { axisLabel, max, min, offset } = axis

  // eslint-disable-next-line consistent-return
  return {
    title: {
      ...DEFAULT_TITLE_OPTIONS,
      display: !!axisLabel,
      text: axisLabel,
    },
    grid: {
      display: showGridLines,
      ...DEFAULT_GRID_OPTIONS,
    },
    border: DEFAULT_BORDER_OPTIONS,
    min,
    max,
    ticks: DEFAULT_TICK_OPTIONS,
    offset: offset,
    position,
  }
}

export function createScales(
  axisOptions: [
    XAxisOptions | undefined,
    YAxisOptions | undefined,
    YAxisOptions | undefined,
  ],
  showGridLines = true,
  xAxisLabels: Array<string> = [],
  hasDecimalYAxisTicks = false,
) {
  const [xAxis, yAxis, y1Axis] = axisOptions

  let x = createScaleOptions(xAxis, showGridLines)
  const y = createScaleOptions(yAxis, showGridLines)
  const y1 = createScaleOptions(y1Axis, showGridLines, 'right')

  if (typeof x !== 'undefined') {
    x.suggestedMax = xAxisLabels.at(-1)

    // Auto-detect time series
    if (xAxisLabels.every(isValidISODate)) {
      x = {
        ...x,
        type: 'time',
        time: {
          unit: xAxis?.timeUnit,
          ...TIME_SERIES_SCALE_TIME_OPTIONS,
        },
        ticks: {
          ...x.ticks,
          callback: (value, index, values) =>
            formatTimeTick(xAxis?.timeUnit, value, index, values),
          ...TIME_SERIES_SCALE_TICKS_OPTIONS,
        },
        grid: {
          ...x.grid,
          ...TIME_SERIES_SCALE_GRID_OPTIONS,
        },
      }
    }
  }

  if (typeof y !== 'undefined') {
    y.ticks = {
      ...y.ticks,
      callback: (value) =>
        formatTick(value, yAxis?.tickFormat, hasDecimalYAxisTicks ? 2 : 0),
    }
  }

  return {
    ...(x && { x }),
    ...(y && { y }),
    ...(y1 && { y1 }),
  }
}
